import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Input,
  InputLeftElement,
  InputGroup,
  Icon,
  Flex,
  IconButton,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
  Spinner,
  Select,
  Skeleton,
  Badge,
  Tooltip,
  useColorModeValue,
  VStack,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { AddIcon, SearchIcon } from '@chakra-ui/icons';
import { PiBuildingApartmentFill } from 'react-icons/pi';
import { FaShieldAlt, FaFilter } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import baseUrl from 'Base_Url/baseUrl';

const MotionBox = motion(Box);
const MotionTr = motion(Tr);

function Addscheme() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [departments, setDepartments] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [schemeName, setSchemeName] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const token = localStorage.getItem('token');

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const hoverBg = useColorModeValue('gray.50', 'gray.900');
  const headerGradient = useColorModeValue(
    'linear(to-r, green.500, teal.500)',
    'linear(to-r, green.600, teal.600)',
  );
  const inputTextColor = useColorModeValue('gray.800', 'white');

  const fetchAllData = useCallback(
    async (search = '') => {
      try {
        setFetching(true);
        const query = new URLSearchParams({ search }).toString();
        const [deptRes, schemeRes] = await Promise.all([
          fetch(`${baseUrl}/master/all-department`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${baseUrl}/master/all-scheme?${query}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const deptData = await deptRes.json();
        const schemeData = await schemeRes.json();

        if (deptRes.ok) {
          const depts = deptData?.data?.data || [];
          setDepartments(depts);
          if (depts.length > 0) setSelectedDeptId(depts[0]._id);
        } else {
          toast({
            title: 'Failed to fetch departments',
            status: 'error',
            duration: 3000,
          });
        }

        if (schemeRes.ok) {
          setSchemes(schemeData?.data?.data || []);
        } else {
          toast({
            title: 'Failed to fetch schemes',
            status: 'error',
            duration: 3000,
          });
        }
      } catch (err) {
        toast({
          title: 'Error fetching data',
          description: err.message,
          status: 'error',
          duration: 3000,
        });
      } finally {
        setFetching(false);
      }
    },
    [token, toast],
  );

  useEffect(() => {
    fetchAllData(searchInput);
  }, [searchInput, fetchAllData]);

  const handleOpenModal = () => {
    if (departments.length > 0) setSelectedDeptId(departments[0]._id);
    onOpen();
    resetForm();
  };

  const handleAddScheme = async () => {
    if (!schemeName.trim() || !selectedDeptId) {
      toast({
        title: 'Scheme name & department are required',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}/master/add-scheme`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: schemeName,
        }),
      });

      const data = await response.json();

      if (response.ok && data?.status) {
        toast({
          title: '✓ Scheme Added',
          description: `${schemeName} has been added successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setSchemeName('');
        onClose();
        fetchAllData(searchInput);
      } else {
        toast({
          title: 'Failed to add scheme',
          description: data.message || 'Something went wrong',
          status: 'error',
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title: 'Error adding scheme',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSchemeName('');
    if (departments.length > 0) setSelectedDeptId(departments[0]._id);
    else setSelectedDeptId('');
  };

  return (
    <>
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <Box
          p={{ base: 4, md: 6 }}
          bg={cardBg}
          borderRadius="2xl"
          boxShadow="0 10px 40px rgba(0, 0, 0, 0.05)"
          border="1px solid"
          borderColor={borderColor}
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-50px"
            left="-50px"
            w="150px"
            h="150px"
            borderRadius="full"
            bgGradient="linear(to-br, green.100, teal.100)"
            opacity={0.1}
          />

          <Flex
            justify="space-between"
            align="center"
            mb={3}
            position="relative"
            zIndex={1}
          >
            <Flex align="center" gap={3}>
              <Flex
                align="center"
                justify="center"
                w="50px"
                h="50px"
                borderRadius="xl"
                bgGradient="linear(to-br, green.500, teal.600)"
                boxShadow="0 8px 25px rgba(56, 178, 172, 0.3)"
              >
                <Icon as={FaShieldAlt} boxSize={6} color="white" />
              </Flex>
              <VStack align="start" spacing={0}>
                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                  Schemes
                </Text>
                <Text fontSize="sm" color={subtitleColor}>
                  Manage system schemes
                </Text>
              </VStack>
            </Flex>

            <HStack>
              <Badge
                colorScheme="green"
                fontSize="xs"
                px={3}
                py={1}
                borderRadius="full"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Text>{schemes.length}</Text>
                <Text>Total</Text>
              </Badge>
              {/* <Tooltip label="Add New Scheme" hasArrow>
                <IconButton
                  colorScheme="green"
                  size="md"
                  icon={<AddIcon />}
                  aria-label="Add Scheme"
                  borderRadius="full"
                  bgGradient="linear(to-r, green.500, teal.500)"
                  _hover={{
                    bgGradient: 'linear(to-r, green.600, teal.600)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 10px 20px rgba(56, 178, 172, 0.4)',
                  }}
                  transition="all 0.2s"
                  onClick={handleOpenModal}
                />
              </Tooltip> */}
            </HStack>
          </Flex>

          <Divider mb={3} />

          <Flex mb={6} position="relative" zIndex={1}>
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search schemes..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                borderRadius="xl"
                border="2px solid"
                color={inputTextColor}
                borderColor={borderColor}
                _focus={{
                  borderColor: 'green.500',
                  boxShadow: '0 0 0 3px rgba(56, 178, 172, 0.15)',
                }}
                _hover={{ borderColor: 'gray.300' }}
              />
            </InputGroup>
          </Flex>

          <Box
            borderRadius="xl"
            overflow="hidden"
            border="1px solid"
            borderColor={borderColor}
            position="relative"
            zIndex={1}
          >
            {fetching ? (
              <Box p={6}>
                <VStack spacing={3} align="stretch">
                  {[...Array(5)].map((_, i) => (
                    <Flex key={i} gap={4}>
                      <Skeleton height="40px" width="50px" borderRadius="md" />
                      <Skeleton height="40px" flex="1" borderRadius="md" />
                      <Skeleton height="40px" flex="1" borderRadius="md" />
                    </Flex>
                  ))}
                </VStack>
              </Box>
            ) : schemes.length === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                p={10}
                minH="200px"
              >
                <Box
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  bg="gray.100"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={4}
                >
                  <Icon as={FaShieldAlt} w={8} h={8} color="gray.400" />
                </Box>
                <Text
                  fontSize="lg"
                  fontWeight="medium"
                  color={textColor}
                  mb={2}
                >
                  No schemes found
                </Text>
                <Text color={subtitleColor} textAlign="center" fontSize="sm">
                  {searchInput
                    ? `No schemes match "${searchInput}"`
                    : 'Start by adding your first scheme'}
                </Text>
              </Flex>
            ) : (
              <Box overflow="auto" height={'30vh'}>
                <Table variant="simple">
                  <Thead bg={headerGradient}>
                    <Tr>
                      <Th
                        color="gray"
                        fontSize="sm"
                        fontWeight="bold"
                        py={4}
                        textAlign="center"
                      >
                        #
                      </Th>
                      <Th color="gray" fontSize="sm" fontWeight="bold" py={4}>
                        SCHEME NAME
                      </Th>
                      <Th color="gray" fontSize="sm" fontWeight="bold" py={4}>
                        DEPARTMENT
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <AnimatePresence>
                      {schemes.map((scheme, index) => (
                        <MotionTr
                          key={scheme._id || index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                          _hover={{
                            bg: hoverBg,
                            transition: 'background 0.2s',
                          }}
                        >
                          <Td
                            textAlign="center"
                            fontSize="sm"
                            fontWeight="medium"
                            color={textColor}
                            py={3}
                          >
                            <Badge
                              colorScheme="green"
                              variant="subtle"
                              px={2}
                              py={1}
                              borderRadius="md"
                            >
                              {index + 1}
                            </Badge>
                          </Td>
                          <Td
                            fontSize="sm"
                            fontWeight="medium"
                            color={textColor}
                            py={3}
                          >
                            <HStack>
                              <Icon
                                as={FaShieldAlt}
                                color="green.500"
                                boxSize={4}
                              />
                              <Text>{scheme.name}</Text>
                            </HStack>
                          </Td>
                          <Td fontSize="sm" color={subtitleColor} py={3}>
                            <Badge
                              colorScheme="blue"
                              fontSize="xs"
                              px={2}
                              py={1}
                              borderRadius="md"
                            >
                              {scheme.department?.name || '—'}
                            </Badge>
                          </Td>
                        </MotionTr>
                      ))}
                    </AnimatePresence>
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </Box>
      </MotionBox>

      {/* <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <MotionBox
          as={ModalContent}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="0 20px 60px rgba(0, 0, 0, 0.3)"
        >
          <Box
            bgGradient="linear(to-r, green.500, teal.600)"
            py={4}
            px={6}
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top={0}
              right={0}
              bottom={0}
              left={0}
              bg="whiteAlpha.100"
              opacity={0.1}
            />
            <Flex align="center" position="relative" zIndex={1}>
              <Flex
                align="center"
                justify="center"
                w="48px"
                h="48px"
                borderRadius="xl"
                bg="whiteAlpha.900"
                mr={4}
              >
                <Icon as={AddIcon} boxSize={5} color="green.600" />
              </Flex>
              <VStack align="start" spacing={0}>
                <ModalHeader
                  fontSize="xl"
                  fontWeight="bold"
                  color="white"
                  p={0}
                  mb={1}
                >
                  New Scheme
                </ModalHeader>
                <Text fontSize="sm" color="whiteAlpha.800">
                  Add a new scheme to the system
                </Text>
              </VStack>
            </Flex>
            <ModalCloseButton
              color="white"
              _hover={{ bg: 'whiteAlpha.200' }}
              _focus={{ boxShadow: 'none' }}
            />
          </Box>

          <ModalBody p={6} bg={hoverBg}>
            <VStack spacing={5}>
              <FormControl isRequired>
                <FormLabel
                  fontSize="sm"
                  fontWeight="medium"
                  color={textColor}
                  mb={2}
                >
                  <HStack>
                    <Icon as={PiBuildingApartmentFill} color="blue.500" />
                    <Text>Department</Text>
                  </HStack>
                </FormLabel>
                <Select
                  value={selectedDeptId}
                  onChange={(e) => setSelectedDeptId(e.target.value)}
                  borderRadius="lg"
                  border="2px solid"
                  borderColor={borderColor}
                  _focus={{
                    borderColor: 'green.500',
                    boxShadow: '0 0 0 3px rgba(56, 178, 172, 0.15)',
                  }}
                  _hover={{ borderColor: 'gray.300' }}
                >
                  {departments.map((dept) => (
                    <option key={dept._id} value={dept._id}>
                      {dept.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel
                  fontSize="sm"
                  fontWeight="medium"
                  color={textColor}
                  mb={2}
                >
                  <HStack>
                    <Icon as={FaShieldAlt} color="green.500" />
                    <Text>Scheme Name</Text>
                  </HStack>
                </FormLabel>
                <Input
                  placeholder="Enter scheme name"
                  value={schemeName}
                  onChange={(e) => setSchemeName(e.target.value)}
                  borderRadius="lg"
                  border="2px solid"
                  borderColor={borderColor}
                  _focus={{
                    borderColor: 'green.500',
                    boxShadow: '0 0 0 3px rgba(56, 178, 172, 0.15)',
                  }}
                  _hover={{ borderColor: 'gray.300' }}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter
            bg={useColorModeValue('gray.100', 'gray.800')}
            borderTop="1px solid"
            borderColor={borderColor}
            p={5}
          >
            <HStack spacing={3} w="full">
              <Button
                variant="outline"
                colorScheme="gray"
                flex="1"
                borderRadius="lg"
                onClick={onClose}
                _hover={{ transform: 'translateY(-1px)' }}
                transition="all 0.2s"
              >
                Cancel
              </Button>
              <Button
                colorScheme="green"
                flex="1"
                borderRadius="lg"
                onClick={handleAddScheme}
                isLoading={loading}
                loadingText="Adding..."
                bgGradient="linear(to-r, green.500, teal.600)"
                _hover={{
                  bgGradient: 'linear(to-r, green.600, teal.700)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 10px 20px rgba(56, 178, 172, 0.4)',
                }}
                _active={{ transform: 'translateY(0)' }}
                transition="all 0.2s"
                leftIcon={<AddIcon />}
              >
                Add Scheme
              </Button>
            </HStack>
          </ModalFooter>
        </MotionBox>
      </Modal> */}
    </>
  );
}

export default Addscheme;
